// components/userManagement/UserListTab.js
import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch } from 'react-redux';
import { updateUser, deleteUser, fetchUsers } from '../../slices/usersSlice';
import { useToast } from '../../context/ToastContext';

const UserListTab = ({ users, onRefresh }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);

  const handleToggleActive = async (user) => {
    try {
      await dispatch(updateUser({
        id: user.id,
        is_active: !user.is_active
      })).unwrap();
      showToast('تم تحديث حالة المستخدم بنجاح', 'success');
      onRefresh();
    } catch (error) {
      showToast('حدث خطأ أثناء التحديث', 'error');
    }
  };

  const handleDelete = async () => {
    if (deleteDialog) {
      try {
        await dispatch(deleteUser(deleteDialog.id)).unwrap();
        showToast('تم حذف المستخدم بنجاح', 'success');
        setDeleteDialog(null);
        onRefresh();
      } catch (error) {
        showToast('حدث خطأ أثناء الحذف', 'error');
      }
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    // When roleFilter is set, only show users with that exact role
    // If roleFilter is 'teacher', only show users with role === 'teacher'
    const matchesRole = !roleFilter || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const roleLabels = {
    manager: 'مدير',
    employee: 'موظف',
    teacher: 'معلم',
    parent: 'ولي أمر',
    student: 'طالب',
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack useFlexGap direction="row" spacing={2} mb={3}>
        <TextField
          label="بحث"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
        />
        <TextField
          select
          label="الصلاحية"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">الكل</MenuItem>
          {Object.entries(roleLabels).map(([value, label]) => (
            <MenuItem key={value} value={value}>{label}</MenuItem>
          ))}
        </TextField>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>اسم المستخدم</TableCell>
              <TableCell>الاسم الكامل</TableCell>
              <TableCell>البريد الإلكتروني</TableCell>
              <TableCell>الصلاحية</TableCell>
              <TableCell>الكيان المرتبط</TableCell>
              <TableCell>الحالة</TableCell>
              <TableCell>الإجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.full_name || '-'}</TableCell>
                <TableCell>{user.email || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={roleLabels[user.role] || user.role}
                    size="small"
                    color={user.role === 'manager' ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>{user.linked_entity_name || '-'}</TableCell>
                <TableCell>
                  <Switch
                    checked={user.is_active}
                    onChange={() => handleToggleActive(user)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => showToast('ميزة التعديل قيد التطوير', 'info')}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteDialog(user)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من حذف المستخدم {deleteDialog?.username}؟
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>إلغاء</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserListTab;
